import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { moduleActions } from '../actions/module.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class ModuleEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getModule$ = createEffect(() => {
        return this.actions.pipe(
            ofType(moduleActions.getModule),
            mergeMap(({ id }) => {
                return this.httpService.getModule(id).pipe(
                    finalizeLoader('module-get'),
                    map((module) => moduleActions.getModuleSuccess({ payload: module })),
                    catchError((response: HttpErrorResponse) => of(moduleActions.getModuleError({ code: response.status }))),
                );
            }),
        );
    });

    createModule$ = createEffect(() => {
        return this.actions.pipe(
            ofType(moduleActions.createModule),
            mergeMap(({ value }) => {
                return this.httpService.createModule(value).pipe(
                    finalizeLoader('module-create'),
                    map((module) => moduleActions.createModuleSuccess({ payload: module })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.modules.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.modules.createError');
                        return of(moduleActions.createModuleError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateModule$ = createEffect(() => {
        return this.actions.pipe(
            ofType(moduleActions.updateModule),
            mergeMap(({ id, value }) => {
                return this.httpService.updateModule(id, value).pipe(
                    finalizeLoader('module-update'),
                    map((module) => moduleActions.updateModuleSuccess({ payload: module })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.modules.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.modules.updateError');
                        return of(moduleActions.updateModuleError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteModule$ = createEffect(() => {
        return this.actions.pipe(
            ofType(moduleActions.deleteModule),
            mergeMap(({ id }) => {
                return this.httpService.deleteModule(id).pipe(
                    finalizeLoader('module-delete'),
                    map(() => moduleActions.deleteModuleSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.modules.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.modules.deleteError');
                        return of(moduleActions.deleteModuleError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
